import React, { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Send, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { collectReferencesFromMarkdown } from "@/lib/contentReferences";
import { imageFilesFromPaste } from "@/lib/parseVideoEmbed";
import { useIsMobile } from "@/hooks/use-mobile";
import EmojiInsertButton from "./EmojiInsertButton";
import MediaAttachBar from "./MediaAttachBar";
import RichMarkdownEditor, {
  isMarkdownEmpty,
  normalizeEditorMarkdown,
} from "./RichMarkdownEditor";

const CommentComposer = ({
  onSubmit,
  onCancel,
  placeholder = "Write a comment...",
  isSubmitting = false,
  className,
  orgId,
  accessToken,
  /** Circa Social / community comments use the Social Giphy proxy (no org_id) */
  social = false,
}) => {
  const isMobile = useIsMobile();
  const [content, setContent] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [gif, setGif] = useState(null);
  const editorRef = useRef(null);
  const allowImageAttach = !social;

  const referenceContext = useMemo(
    () =>
      orgId && accessToken
        ? {
            enabled: true,
            orgId,
            accessToken,
            contextType: "comment",
          }
        : null,
    [orgId, accessToken]
  );

  const hasMedia = Boolean(
    (allowImageAttach && pendingFiles.length) || gif
  );

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const body = normalizeEditorMarkdown(content);
    if ((isMarkdownEmpty(body) && !hasMedia) || isSubmitting) return;

    try {
      await onSubmit(body, {
        references: collectReferencesFromMarkdown(body, orgId),
        gif,
        file: allowImageAttach ? pendingFiles[0] || null : null,
      });
      setContent("");
      setPendingFiles([]);
      setGif(null);
      editorRef.current?.clear();
      requestAnimationFrame(() => editorRef.current?.focus("end"));
    } catch {
      // Preserve draft on failure
    }
  };

  const handleCancel = () => {
    setContent("");
    setPendingFiles([]);
    setGif(null);
    editorRef.current?.clear();
    if (onCancel) {
      onCancel();
    }
  };

  const onEditorPaste = (e) => {
    if (!allowImageAttach) return;
    const images = imageFilesFromPaste(e.nativeEvent || e);
    if (!images.length) return;
    setPendingFiles(images.slice(0, 1));
    setGif(null);
  };

  return (
    <div className={cn("border-t border-border pt-4", className)}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div onPaste={onEditorPaste}>
          <RichMarkdownEditor
            ref={editorRef}
            value={content}
            onChange={setContent}
            onSubmit={handleSubmit}
            submitMode="mod-enter"
            placeholder={placeholder}
            disabled={isSubmitting}
            minRows={isMobile ? 2 : 3}
            toolbar={isMobile ? "never" : "always"}
            referenceContext={referenceContext}
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <MediaAttachBar
            className="min-w-0"
            mode="comment"
            pendingFiles={pendingFiles}
            onFilesChange={setPendingFiles}
            gif={gif}
            onGifChange={setGif}
            allowGiphy
            showAttachButton={allowImageAttach}
            social={social}
            orgId={orgId}
            disabled={isSubmitting}
            trailingActions={
              <EmojiInsertButton
                onInsert={(native) => editorRef.current?.insertText(native)}
                disabled={isSubmitting}
              />
            }
          />
          <div className="flex gap-2 shrink-0 ml-auto">
            {onCancel && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              size="sm"
              disabled={(isMarkdownEmpty(content) && !hasMedia) || isSubmitting}
            >
              <Send className="h-4 w-4 mr-1" />
              {isSubmitting ? "Posting..." : "Post Comment"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CommentComposer;
